import { useEffect } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { updateDepartment, getDepartmentById } from "../../../api/users";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import withPermission from "../../../core/common/autorisations/withPermission";

//Typage des props parents
type UUID = string;
type Props = {
  show: boolean;
  departmentID: UUID;
  onClose: () => void;
  onSaved?: () => void;
};

const EditDepartmentModal = ({
  departmentID,
  show,
  onClose,
  onSaved,
}: Props) => {
  //Schéma de notre formulaire
  const createSchema = z.object({
    label: z.string().nonempty("Le libellé du département est requis"),
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      //Envoi des datas pour création vers le backend
      await updateDepartment(data, departmentID);

      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Département mis à jour !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    if (!show || !departmentID) return; // ✅ Ne rien faire si le modal n'est pas ouvert

    const fetchDepartment = async () => {
      try {
        const response = await getDepartmentById(departmentID);
        // console.log(response)
        if (!response) {
          // Si aucune année trouvé
          throw new Error("Département introuvable");
        }

        reset({
          // Si année trouvé on rempli les champs du formulaire
          label: response.data.label,
        });
      } catch (error: any) {
        // Si erreur (Toast Error)
        toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
      }
    };

    fetchDepartment();
  }, [show, departmentID, reset]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Modifer un Département</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Nom du département
                  </label>
                  <input
                    {...register("label")}
                    type="text"
                    className={
                      errors.label ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: Rectorat, Comptabilité"
                  />
                  {errors.label && (
                    <div className="invalid-feedback">
                      {errors.label.message}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  EditDepartmentModal,
  [buildPermission(Resource.DEPARTMENT, Action.UPDATE)],
  "masquer",
);
